import { format, parseISO } from "date-fns";
import { DefaultCashBasisAccountCodes } from "@common/constants/domains/accounting/accounts";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { RevenueSubMode } from "@common/types/domains/accounting/schedule";
import {
    KickDefaultScheduleAccountCandidate,
    resolveKickDefaultScheduleAccountIds,
} from "./resolveKickDefaultScheduleAccounts";

/**
 * A migrated schedule's balance came in with the opening balances, so its
 * releases can only start once the books do. Blank (not yet entered) is left
 * to the draft's own checks.
 */
export function getMigratedScheduleStartError(options: {
    termStart: string;
    bookkeepingStartDate: string;
}): string | null {
    const { termStart, bookkeepingStartDate } = options;
    if (!termStart || termStart >= bookkeepingStartDate) {
        return null;
    }
    const formattedStart = format(
        parseISO(bookkeepingStartDate),
        fullDateFullYearFormat,
    );
    return `Start on or after ${formattedStart}, your bookkeeping start date. Earlier months were recognized in your previous books.`;
}

/**
 * Where a migrated revenue balance sits: Kick's default Deferred Revenue
 * account. A chart brought over as-is has no such account, so the user picks
 * the one that holds the balance.
 */
export function resolveMigratedScheduleHoldingAccountId(options: {
    revenueSubMode: RevenueSubMode;
    accounts: readonly KickDefaultScheduleAccountCandidate[];
}): string | null {
    const defaultAccountIds = resolveKickDefaultScheduleAccountIds(options);
    if (defaultAccountIds === null) {
        return null;
    }
    const isKickDeferredRevenue = options.accounts.some(
        (account) =>
            account.id === defaultAccountIds.holdingAccountId &&
            account.kickOriginalCode ===
                DefaultCashBasisAccountCodes.deferredRevenue,
    );
    return isKickDeferredRevenue ? defaultAccountIds.holdingAccountId : null;
}
